import type { GooglePlaceDetails } from "./types";

export const stripWebsiteForDisplay = (uri: string): string =>
  uri.trim().replace(/^https?:\/\//i, "").replace(/\/$/, "");

export const streetLineFromFormattedAddress = (formatted: string): string =>
  formatted.split(",")[0]?.trim() ?? formatted.trim();

export const resolveStreetLine = (
  details: Pick<GooglePlaceDetails, "streetLine" | "formattedAddress">,
): string =>
  details.streetLine.trim() ||
  streetLineFromFormattedAddress(details.formattedAddress);

const buildStreetLine = (
  streetNumber: string,
  route: string,
  formattedAddress: string,
): string => {
  const joined = [streetNumber, route].filter(Boolean).join(" ").trim();
  if (joined) return joined;
  return streetLineFromFormattedAddress(formattedAddress);
};

export const normalizeWebsiteForStorage = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
};

type AddressComponent = {
  types?: string[];
  longText?: string;
  shortText?: string;
};

const pickComponent = (
  components: AddressComponent[] | undefined,
  type: string,
  useShort = false,
) => {
  const match = components?.find((c) => c.types?.includes(type));
  if (!match) return "";
  return (useShort ? match.shortText : match.longText) ?? "";
};

const CITY_COMPONENT_TYPES = [
  "locality",
  "postal_town",
  "sublocality",
  "sublocality_level_1",
  "neighborhood",
] as const;

const resolveCityFromComponents = (
  pick: (type: string) => string,
): string => {
  for (const type of CITY_COMPONENT_TYPES) {
    const value = pick(type).trim();
    if (value) return value;
  }
  return "";
};

type LegacyAddressComponent = {
  types?: string[];
  long_name?: string;
  short_name?: string;
};

const pickLegacyComponent = (
  components: LegacyAddressComponent[] | undefined,
  type: string,
  useShort = false,
) => {
  const match = components?.find((c) => c.types?.includes(type));
  if (!match) return "";
  return (useShort ? match.short_name : match.long_name) ?? "";
};

/** Maps legacy Place Details JSON (`maps.googleapis.com/maps/api/place/details`). */
export const mapPlaceDetailsFromLegacyApi = (
  placeId: string,
  payload: Record<string, unknown>,
): GooglePlaceDetails => {
  const components = payload.address_components as
    | LegacyAddressComponent[]
    | undefined;

  return {
    placeId,
    name: String(payload.name ?? "").trim(),
    formattedAddress: String(payload.formatted_address ?? "").trim(),
    streetLine: buildStreetLine(
      pickLegacyComponent(components, "street_number"),
      pickLegacyComponent(components, "route"),
      String(payload.formatted_address ?? "").trim(),
    ),
    phone: String(payload.formatted_phone_number ?? "").trim(),
    website: stripWebsiteForDisplay(String(payload.website ?? "")),
    googleMapsUri: String(payload.url ?? "").trim(),
    city: resolveCityFromComponents((type) =>
      pickLegacyComponent(components, type),
    ),
    stateAbbr: pickLegacyComponent(components, "administrative_area_level_1", true),
    zipcode: pickLegacyComponent(components, "postal_code"),
    country: pickLegacyComponent(components, "country", true),
  };
};

export const mapPlaceDetailsFromApi = (
  placeId: string,
  payload: Record<string, unknown>,
): GooglePlaceDetails => {
  const displayName = payload.displayName as { text?: string } | undefined;
  const components = payload.addressComponents as AddressComponent[] | undefined;
  const photos = payload.photos as Array<{ name?: string }> | undefined;
  const photoName = String(photos?.[0]?.name ?? "").trim() || undefined;

  return {
    placeId,
    name: String(displayName?.text ?? "").trim(),
    formattedAddress: String(payload.formattedAddress ?? "").trim(),
    streetLine: buildStreetLine(
      pickComponent(components, "street_number"),
      pickComponent(components, "route"),
      String(payload.formattedAddress ?? "").trim(),
    ),
    phone: String(
      payload.nationalPhoneNumber ?? payload.internationalPhoneNumber ?? "",
    ).trim(),
    website: stripWebsiteForDisplay(String(payload.websiteUri ?? "")),
    googleMapsUri: String(payload.googleMapsUri ?? "").trim(),
    city: resolveCityFromComponents((type) =>
      pickComponent(components, type),
    ),
    stateAbbr: pickComponent(components, "administrative_area_level_1", true),
    zipcode: pickComponent(components, "postal_code"),
    country: pickComponent(components, "country", true),
    photoName,
    rating:
      typeof payload.rating === "number" && Number.isFinite(payload.rating)
        ? payload.rating
        : undefined,
    reviewCount:
      typeof payload.userRatingCount === "number" &&
      Number.isFinite(payload.userRatingCount)
        ? payload.userRatingCount
        : undefined,
  };
};
